/**
 * PremiumOrb — the single, unified play-screen orb used site-wide.
 *
 * Replaces the patchwork of `animate-pulse-slow`, `animate-pulse-amber`,
 * `animate-scale-pulse`, motion.div + CSS-keyframe competition that made
 * the Private Suite session animation feel jumpy.
 *
 * Feb 12, 2026 — user feedback: "Audio player is still jumping —
 * graphics are lifting up and down. Slow the orb right down so it has
 * a gentle pulse." Resolution:
 *   • One universal cadence regardless of mode (binaural is FELT in the
 *     audio, not seen in the visual — decoupled the Hz coupling that
 *     used to throb the orb).
 *   • Slowed breath cycle from 5.5s → 9s.
 *   • Tightened scale range from 1.08 → 1.025 so the surrounding text
 *     never visibly reflows.
 *   • Opacity range tightened (0.92 → 1) so it reads as a glow, not a
 *     blink.
 *
 * Modes kept for API compatibility but they all resolve to the same
 * gentle pulse (binaural still gets a one-time `static` rest if the
 * caller passes it). The orb is now identity-stable across phases.
 *
 * Props:
 *   size        Diameter in px (default 220).
 *   hue         'gold' (default) | 'cool' | 'neutral' | 'warm'.
 *   mode        'breath' | 'binaural' | 'static'.
 *   hz          Kept for API compat — no longer drives the visual rate.
 *   intensity   0..1 multiplier on the halo (default 1).
 */
import React from 'react';
import { motion } from 'framer-motion';

const TINTS = {
  gold:    { core: 'rgba(248,228,192,0.95)', glow: 'rgba(217,180,120,0.55)', outer: 'rgba(170,140,95,0.18)' },
  cool:    { core: 'rgba(220,235,250,0.90)', glow: 'rgba(170,195,225,0.50)', outer: 'rgba(120,150,190,0.18)' },
  neutral: { core: 'rgba(245,240,230,0.90)', glow: 'rgba(220,210,195,0.45)', outer: 'rgba(180,170,155,0.16)' },
  warm:    { core: 'rgba(255,232,200,0.95)', glow: 'rgba(225,180,140,0.55)', outer: 'rgba(180,140,110,0.18)' },
};

// Single universal cadence. 9 seconds inhale/exhale, micro-scale.
const PULSE_DURATION = 9;
const PULSE_SCALE    = [1, 1.025, 1];
const PULSE_OPACITY  = [0.92, 1, 0.92];

const PremiumOrb = ({
  size = 220,
  hue = 'gold',
  mode = 'breath',
  hz: _hz, // accepted for API compat, intentionally ignored
  intensity = 1,
  testId,
}) => {
  const t = TINTS[hue] || TINTS.gold;
  const isStatic = mode === 'static';

  const animate = isStatic
    ? { scale: 1, opacity: 0.94 }
    : { scale: PULSE_SCALE, opacity: PULSE_OPACITY };

  const transition = isStatic
    ? { duration: 1.2, ease: 'easeOut' }
    : { duration: PULSE_DURATION, repeat: Infinity, ease: 'easeInOut' };

  return (
    <motion.div
      animate={animate}
      transition={transition}
      className="relative rounded-full"
      style={{ width: size, height: size, willChange: 'transform, opacity' }}
      data-testid={testId}
    >
      {/* Outer halo */}
      <div
        aria-hidden
        className="absolute inset-0 rounded-full"
        style={{
          background: `radial-gradient(circle at 50% 50%, ${t.glow} 0%, ${t.outer} 40%, transparent 72%)`,
          filter: `blur(${size * 0.06}px)`,
          transform: `scale(${1.45 * intensity})`,
        }}
      />
      {/* Core orb */}
      <div
        aria-hidden
        className="absolute inset-0 rounded-full"
        style={{
          background: `radial-gradient(circle at 45% 40%, ${t.core} 0%, ${t.glow} 38%, transparent 72%)`,
          boxShadow: `0 0 ${size * 0.4}px ${t.glow}`,
        }}
      />
    </motion.div>
  );
};

// Kept exported for any caller still importing it (BinauralPicker etc.).
// No longer used for visual rate — pulse is universal.
export const BINAURAL_HZ = {
  delta: 2.5,
  theta: 6,
  alpha: 10,
  beta:  18,
  gamma: 40,
};

export default PremiumOrb;
